import { useState, useMemo, type Dispatch, type SetStateAction } from 'react'
import { useDeviceStatus, useEndpoints } from '../hooks/useApi'
import { useMagoMaster } from '../hooks/useMagoMaster'
import { apiClient } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import MagoMasterForm from './MagoMasterForm'
import DeviceDetailsModal from './DeviceDetailsModal'
import { formatDateTime, hasValidValue } from '../utils/format'
import type { DeviceStatusResponse } from '../types/api'
import type { MagoMasterResponse } from '../types/master'

export type SortField = keyof DeviceStatusResponse
export type SortOrder = 'asc' | 'desc'

export type StatusFilterValue = 'all' | 'online' | 'offline'

interface DevicesListProps {
  onSendMessage: (deviceId: string, endpoint: string) => void
  searchTerm: string
  setSearchTerm: (value: string) => void
  statusFilter: StatusFilterValue
  setStatusFilter: (value: StatusFilterValue) => void
  sortField: SortField
  setSortField: (value: SortField) => void
  sortOrder: SortOrder
  setSortOrder: (value: SortOrder) => void
  currentPage: number
  setCurrentPage: Dispatch<SetStateAction<number>>
}

export default function DevicesList({
  onSendMessage,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  sortField,
  setSortField,
  sortOrder,
  setSortOrder,
  currentPage,
  setCurrentPage,
}: DevicesListProps) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  const devicesPerPage = 400
  const offset = (currentPage - 1) * devicesPerPage

  const {
    data: devices,
    loading,
    error,
    refetch,
  } = useDeviceStatus({
    limit: devicesPerPage,
    offset: offset,
  })
  // @ts-ignore - magoDevices is used in handleEditSuccess for refetching
  const { devices: magoDevices, refetch: refetchMagoDevices } = useMagoMaster()
  const { data: endpointsData } = useEndpoints()
  const [selectedDevice, setSelectedDevice] = useState<DeviceStatusResponse | null>(null)
  const [editingDevice, setEditingDevice] = useState<MagoMasterResponse | null>(null)
  const [showEditForm, setShowEditForm] = useState(false)
  const [lastMessageTime, setLastMessageTime] = useState<Record<string, number>>({})

  const handleRefresh = () => {
    // ページやソート状態は維持したままデータのみ更新
    refetch()
    refetchMagoDevices()
  }

  const handleEditDevice = async (magoId: string) => {
    try {
      // Fetch fresh data directly from API with cache-busting
      const API_BASE_URL = import.meta.env.VITE_API_URL || ''
      const timestamp = Date.now()
      const response = await fetch(`${API_BASE_URL}/api/mago?_t=${timestamp}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch latest device data')
      }
      const freshMagoDevices = await response.json()

      const magoDevice = freshMagoDevices.find((d: any) => d.mago_id === magoId)
      if (magoDevice) {
        console.log('Setting fresh device data:', magoDevice)
        setEditingDevice(magoDevice)
        setShowEditForm(true)
      } else {
        // Device not found in master data
        alert('デバイスがマスターデータに見つかりません')
      }
    } catch (error) {
      console.error('Error finding device:', error)
      alert('デバイスの検索中にエラーが発生しました')
    }
  }

  const handleEditSuccess = async () => {
    setShowEditForm(false)
    setEditingDevice(null)
    // Refresh both device status and master data with cache busting
    try {
      console.log('Refreshing data after edit success...')

      // Force refresh with a small delay to ensure backend is updated
      setTimeout(async () => {
        await Promise.all([refetch(), refetchMagoDevices()])
        console.log('Data refreshed after edit')
      }, 200)
    } catch (error) {
      console.error('Error refreshing data after edit:', error)
    }
  }

  const handleEditClose = () => {
    setShowEditForm(false)
    setEditingDevice(null)
  }

  // Endpoint name compatibility mapping (old name -> new name)
  // For backward compatibility when endpoint names are changed in config.yml
  const ENDPOINT_NAME_MAPPING: Record<string, string> = {
    yuinet: 'yuinet_koshimizu_iwate',
    koshimizu: 'yuinet_koshimizu_iwate',
  }

  const handleSendMessage = async (deviceId: string) => {
    // Prevent double-click (minimum 3 seconds between message buttons for same device)
    const now = Date.now()
    if (lastMessageTime[deviceId] && now - lastMessageTime[deviceId] < 3000) {
      return
    }
    setLastMessageTime(prev => ({ ...prev, [deviceId]: now }))

    let selectedEndpoint = 'broker1' // fallback

    try {
      // Try to get the device's latest log to determine the correct endpoint
      const latestLog = await apiClient.getLatestLog(deviceId)
      if (latestLog && latestLog.endpoint) {
        let endpointName = latestLog.endpoint

        // Check if endpoint exists in current config, if not try mapping
        if (endpointsData && !endpointsData.endpoints[endpointName]) {
          const mappedName = ENDPOINT_NAME_MAPPING[endpointName]
          if (mappedName && endpointsData.endpoints[mappedName]) {
            endpointName = mappedName
            console.log(`Endpoint name mapped: ${latestLog.endpoint} -> ${mappedName}`)
          }
        }

        // Verify the endpoint is still connected
        if (endpointsData && endpointsData.endpoints[endpointName]?.connected) {
          selectedEndpoint = endpointName
        }
      }
    } catch (error) {
      console.warn('Failed to get latest log for device:', deviceId, error)
    }

    // If we don't have a good endpoint from logs, fall back to first connected endpoint
    if (selectedEndpoint === 'broker1' && endpointsData && endpointsData.endpoints) {
      const connectedEndpoints = Object.entries(endpointsData.endpoints)
        .filter(([_, endpoint]) => endpoint.connected)
        .map(([name, _]) => name)

      if (connectedEndpoints.length > 0) {
        selectedEndpoint = connectedEndpoints[0]
      }
    }

    onSendMessage(deviceId, selectedEndpoint)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const filteredAndSortedDevices = useMemo(() => {
    if (!devices) return []

    const filtered = devices.filter(device => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        const matchesDeviceId = device.mago_id.toLowerCase().includes(term)
        const matchesSimId = device.sim_id && device.sim_id.toLowerCase().includes(term)
        const matchesGroupId = device.group_id && device.group_id.toLowerCase().includes(term)
        const matchesDestName = device.dest_name && device.dest_name.toLowerCase().includes(term)

        // Check Sub Destination
        const magoDevice = magoDevices?.find(d => d.mago_id === device.mago_id)
        const matchesSubDestName =
          magoDevice?.sub_dest_name && magoDevice.sub_dest_name.toLowerCase().includes(term)

        if (
          !matchesDeviceId &&
          !matchesSimId &&
          !matchesGroupId &&
          !matchesDestName &&
          !matchesSubDestName
        ) {
          return false
        }
      }
      // Status filter
      if (statusFilter === 'online' && !device.is_online) return false
      if (statusFilter === 'offline' && device.is_online) return false
      return true
    })

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[sortField]
      const bValue = b[sortField]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        comparison = aValue === bValue ? 0 : aValue ? -1 : 1
      }

      return sortOrder === 'asc' ? comparison : -comparison
    })

    return filtered
  }, [devices, searchTerm, statusFilter, sortField, sortOrder, magoDevices])

  // Calculate pagination info
  const totalFilteredDevices = filteredAndSortedDevices.length
  const hasMoreDevices = devices && devices.length === devicesPerPage
  const canGoNext = hasMoreDevices
  const canGoPrevious = currentPage > 1

  // Reset to first page when search or filter changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleStatusFilterChange = (value: StatusFilterValue) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className="fas fa-sort text-muted" />
    return sortOrder === 'asc' ? (
      <i className="fas fa-sort-up text-primary" />
    ) : (
      <i className="fas fa-sort-down text-primary" />
    )
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">読み込み中...</span>
        </div>
        <p className="mt-2 text-muted">端末データを読み込んでいます...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="fas fa-exclamation-triangle me-2" />
        端末読み込みエラー: {error}
      </div>
    )
  }

  return (
    <>
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-3">
              <label className="form-label">端末検索</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="端末ID、グループID、SIM ID、運用先、利用先で検索..."
                  value={searchTerm}
                  onChange={e => handleSearchChange(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">ステータスフィルタ</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={e => handleStatusFilterChange(e.target.value as StatusFilterValue)}
              >
                <option value="all">全端末</option>
                <option value="online">オンラインのみ</option>
                <option value="offline">オフラインのみ</option>
              </select>
            </div>
            <div className="col-md-7">
              <div className="form-label text-muted">
                ページ {currentPage} - 表示中 {totalFilteredDevices} 端末
                {totalFilteredDevices !== 1 ? '(複数)' : ''}{' '}
                {hasMoreDevices ? '(さらに利用可能)' : ''}
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  disabled={!canGoPrevious}
                >
                  <i className="fas fa-chevron-left me-1" />
                  前へ
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!canGoNext}
                >
                  次へ
                  <i className="fas fa-chevron-right ms-1" />
                </button>
                <button type="button" className="btn btn-primary" onClick={handleRefresh}>
                  <i className="fas fa-sync-alt me-2" />
                  更新
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="fas fa-microchip me-2" />
            端末ステータスモニター
          </h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-striped mb-0 device-table">
              <thead className="table-dark">
                <tr>
                  <th scope="col" className="sortable" onClick={() => handleSort('mago_id')}>
                    端末ID {getSortIcon('mago_id')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('is_online')}>
                    ステータス {getSortIcon('is_online')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('last_seen')}>
                    最終確認 {getSortIcon('last_seen')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('group_id')}>
                    グループID {getSortIcon('group_id')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('sim_id')}>
                    SIM ID {getSortIcon('sim_id')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('dest_name')}>
                    運用先 {getSortIcon('dest_name')}
                  </th>
                  <th
                    scope="col"
                    className="sortable"
                    onClick={() => handleSort('firmware_version')}
                  >
                    ファームウェアバージョン {getSortIcon('firmware_version')}
                  </th>
                  <th scope="col" className="text-center">
                    電源状態
                  </th>
                  <th
                    scope="col"
                    className="sortable text-center"
                    onClick={() => handleSort('consecutive_playback_failures')}
                  >
                    再生失敗 {getSortIcon('consecutive_playback_failures')}
                  </th>
                  <th scope="col" className="text-center">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedDevices.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-muted">
                      <i className="fas fa-info-circle me-2" />
                      条件に一致する端末が見つかりません
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedDevices.map(device => (
                    <tr key={device.mago_id} className={device.is_online ? '' : 'table-secondary'}>
                      <td className="fw-bold">{device.mago_id}</td>
                      <td>
                        <span className={`badge ${device.is_online ? 'bg-success' : 'bg-danger'}`}>
                          <i className={`fas fa-circle me-1 ${device.is_online ? 'blink' : ''}`} />
                          {device.is_online ? 'オンライン' : 'オフライン'}
                        </span>
                      </td>
                      <td className="text-nowrap">
                        <span className="badge bg-light text-dark">
                          {formatDateTime(device.last_seen)}
                        </span>
                      </td>
                      <td>
                        {hasValidValue(device.group_id) ? (
                          <span className="badge bg-secondary">{device.group_id}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {hasValidValue(device.sim_id) ? (
                          <span className="badge bg-info">{device.sim_id}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const magoDevice = magoDevices?.find(d => d.mago_id === device.mago_id)
                          const subDestName = magoDevice?.sub_dest_name
                          const hasDestName = hasValidValue(device.dest_name)
                          const hasSubDestName = hasValidValue(subDestName)

                          if (!hasDestName && !hasSubDestName) {
                            return <span className="text-muted">-</span>
                          }

                          return (
                            <div className="d-flex flex-column gap-1">
                              {hasDestName && (
                                <span className="badge bg-primary">{device.dest_name}</span>
                              )}
                              {hasSubDestName && (
                                <span className="badge bg-secondary">{subDestName}</span>
                              )}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="font-monospace small">
                        {device.firmware_version || <span className="text-muted">-</span>}
                      </td>
                      <td className="text-center">
                        {device.power_state ? (
                          <span
                            className={`badge ${device.power_state === 'ON' ? 'bg-success' : 'bg-secondary'}`}
                          >
                            <i className="fas fa-power-off me-1" />
                            {device.power_state}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        {device.consecutive_playback_failures !== null &&
                        device.consecutive_playback_failures !== undefined ? (
                          device.consecutive_playback_failures > 0 ? (
                            <span
                              className={`badge ${device.consecutive_playback_failures >= 3 ? 'bg-danger' : 'bg-warning text-dark'}`}
                            >
                              <i className="fas fa-exclamation-triangle me-1" />
                              {device.consecutive_playback_failures}回
                            </span>
                          ) : (
                            <span className="badge bg-success">
                              <i className="fas fa-check-circle me-1" />
                              正常
                            </span>
                          )
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleSendMessage(device.mago_id)}
                            title="メッセージ送信"
                            disabled={
                              !!lastMessageTime[device.mago_id] &&
                              Date.now() - lastMessageTime[device.mago_id] < 3000
                            }
                          >
                            <i className="fas fa-paper-plane" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setSelectedDevice(device)}
                            title="詳細を表示"
                          >
                            <i className="fas fa-info-circle" />
                          </button>
                          {isAdmin && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleEditDevice(device.mago_id)}
                              title="マスターデータ編集"
                            >
                              <i className="fas fa-edit" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Pagination */}
        <div className="card-footer">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              ページ {currentPage} - 表示中 {totalFilteredDevices} 端末
              {totalFilteredDevices !== 1 ? '(複数)' : ''}{' '}
              {hasMoreDevices ? '(さらに利用可能)' : ''}
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setCurrentPage(prev => prev - 1)}
                disabled={!canGoPrevious}
              >
                <i className="fas fa-chevron-left me-1" />
                前へ
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={!canGoNext}
              >
                次へ
                <i className="fas fa-chevron-right ms-1" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {selectedDevice && (
        <DeviceDetailsModal
          device={selectedDevice}
          magoDevices={magoDevices}
          onClose={() => setSelectedDevice(null)}
        />
      )}

      {/* Edit Master Data Modal */}
      {showEditForm && (
        <MagoMasterForm
          device={editingDevice}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  )
}
